// pages/Mining.jsx
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  alpha,
  Stack,
  Divider,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  LocationOn,
  TrendingUp,
  Verified,
  Security,
  Diamond,
  Factory,
  Shield,
  WorkspacePremium,
  People,
  Speed,
  Forest,
  WaterDrop,
  Handshake,
  EmojiEvents,
  Timeline,
  GppGood,
  Engineering,
  Analytics,
  ThumbUp,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Premium animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const floatSlow = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(20px, -15px) rotate(2deg);
  }
  66% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatMedium = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(-15px, -10px) rotate(-2deg);
  }
  66% {
    transform: translate(15px, 8px) rotate(1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatFast = keyframes`
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  50% {
    transform: translate(8px, -8px) scale(1.05);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
`;

const shimmerBackground = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0.3;
  }
`;

// Abstract Background Component
const AbstractBackground = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 30%, #0a0a2a 0%, #0d0d35 50%, #060620 100%)',
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '-5%',
        width: '40%',
        height: '40%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatSlow} 25s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatMedium} 30s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, rgba(0,100,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatFast} 20s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(0,150,255,0.06) 0%, rgba(0,150,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatSlow} 22s ease-in-out infinite reverse`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: '200px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
        transform: 'rotate(25deg)',
        animation: `${shimmerBackground} 4s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '25%',
        right: '15%',
        width: '300px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
        transform: 'rotate(-15deg)',
        animation: `${shimmerBackground} 5s ease-in-out infinite 1s`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '60%',
        left: '10%',
        width: '150px',
        height: '150px',
        border: '1px solid rgba(212,175,55,0.08)',
        borderRadius: '50%',
        animation: `${floatMedium} 18s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        right: '25%',
        width: '100px',
        height: '100px',
        border: '1px solid rgba(0,150,255,0.1)',
        borderRadius: '50%',
        animation: `${floatFast} 15s ease-in-out infinite reverse`,
      }}
    />
    
    {[...Array(20)].map((_, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${2 + Math.random() * 4}px`,
          height: `${2 + Math.random() * 4}px`,
          background: `rgba(212, 175, 55, ${0.1 + Math.random() * 0.3})`,
          borderRadius: '50%',
          animation: `${floatFast} ${15 + Math.random() * 20}s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
    ))}
    
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }}
    />
  </Box>
);

// Premium Section Header
const PremiumHeader = ({ title, subtitle, align = 'center' }) => (
  <Box sx={{ mb: 4, textAlign: align }}>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        mb: 1.5,
        px: 2,
        py: 0.5,
        borderRadius: 4,
        background: alpha('#D4AF37', 0.12),
        border: '1px solid rgba(212, 175, 55, 0.25)',
        backdropFilter: 'blur(5px)',
      }}
    >
      <Diamond sx={{ fontSize: 12, color: '#D4AF37' }} />
      <Typography
        sx={{
          color: '#D4AF37',
          fontWeight: 600,
          letterSpacing: 2,
          fontSize: '0.65rem',
          textTransform: 'uppercase',
        }}
      >
        {subtitle}
      </Typography>
      <Diamond sx={{ fontSize: 12, color: '#D4AF37' }} />
    </Box>
    
    <Typography
      variant="h3"
      sx={{
        fontSize: { xs: 28, sm: 34, md: 40 },
        fontWeight: 800,
        background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        mb: 1,
        position: 'relative',
        display: 'inline-block',
        letterSpacing: '-0.02em',
      }}
    >
      {title}
    </Typography>
    
    <Box
      sx={{
        width: 60,
        height: 3,
        background: 'linear-gradient(90deg, #D4AF37, #FFD700, #D4AF37)',
        margin: '0 auto',
        borderRadius: 2,
        mt: 2,
      }}
    />
  </Box>
);

const Mining = () => {
  const mines = [
    {
      name: 'Bushveld Complex',
      location: 'South Africa',
      flag: '🇿🇦',
      metals: ['Platinum', 'Palladium', 'Rhodium', 'Gold'],
      annualOutput: '500,000 oz',
      status: 'Active',
      description: 'One of the world\'s richest PGM deposits with over 100 years of production history.',
      reserves: '45M oz',
      employees: '5,000+',
      established: '1924',
      grade: '4.5 g/t',
      depth: '500-1,200m',
      miningMethod: 'Underground & Open Pit',
    },
    {
      name: 'Malolotja Reserve',
      location: 'Eswatini',
      flag: '🇸🇿',
      metals: ['Gold', 'Silver'],
      annualOutput: '250,000 oz',
      status: 'Expanding',
      description: 'High-grade gold operation with significant exploration upside and modern infrastructure.',
      reserves: '8M oz',
      employees: '1,200+',
      established: '2005',
      grade: '6.2 g/t',
      depth: '200-600m',
      miningMethod: 'Underground',
    },
    {
      name: 'Great Dyke Extension',
      location: 'Zimbabwe',
      flag: '🇿🇼',
      metals: ['Platinum', 'Gold', 'Nickel'],
      annualOutput: '300,000 oz',
      status: 'Development',
      description: 'Strategic expansion into one of Africa\'s most prospective PGM regions.',
      reserves: '12M oz',
      employees: '800+',
      established: '2018',
      grade: '3.8 g/t',
      depth: '300-800m',
      miningMethod: 'Underground',
    },
  ];

  const metrics = [
    { label: 'Total Reserves', value: '65M oz', icon: <Diamond />, description: 'Proven & Probable', detail: 'PGM + Gold Equivalent' },
    { label: 'Annual Production', value: '1.05M oz', icon: <TrendingUp />, description: 'Gold Equivalent', detail: '+15% YoY Growth' },
    { label: 'ESG Rating', value: 'AAA', icon: <GppGood />, description: 'MSCI Rating', detail: 'Top 1% Industry' },
    { label: 'Safety Record', value: '0 LTI', icon: <Security />, description: '3 Years Running', detail: '5M+ Hours Worked' },
  ];

  const sustainabilityMetrics = [
    { label: 'Carbon Neutral', value: '2026', icon: <Verified />, progress: 65, description: 'On track for target' },
    { label: 'Community Investment', value: '$10M+', icon: <Handshake />, progress: 80, description: 'Annual commitment' },
    { label: 'Local Employment', value: '85%', icon: <People />, progress: 85, description: 'Local workforce' },
    { label: 'Water Recycled', value: '95%', icon: <WaterDrop />, progress: 95, description: 'Closed-loop system' },
  ];

  const technologies = [
    { name: 'AI-Powered Exploration', icon: <Speed />, description: 'Machine learning for deposit targeting', efficiency: '+40% discovery rate' },
    { name: 'Autonomous Haulage', icon: <Factory />, description: 'Driverless trucks for safety', efficiency: '0 accidents in 2 years' },
    { name: 'Real-time Monitoring', icon: <Timeline />, description: 'IoT sensors for operations', efficiency: '-25% downtime' },
    { name: 'Carbon Capture', icon: <Forest />, description: 'Greenhouse gas reduction', efficiency: '15,000 tons CO2/year' },
  ];

  const explorationProjects = [
    {
      name: 'Limpopo Belt Extension',
      location: 'South Africa',
      targetMetals: ['Gold', 'Copper'],
      estimatedResource: '3.5M oz',
      stage: 'Advanced Exploration',
      expectedProduction: '2026',
    },
    {
      name: 'Mozambique Coastal Project',
      location: 'Mozambique',
      targetMetals: ['Heavy Minerals', 'Titanium'],
      estimatedResource: '25M tons',
      stage: 'Initial Discovery',
      expectedProduction: '2028',
    },
    {
      name: 'Botswana PGM Project',
      location: 'Botswana',
      targetMetals: ['Platinum', 'Palladium'],
      estimatedResource: '8M oz',
      stage: 'Drilling Phase',
      expectedProduction: '2027',
    },
  ];

  const miningMethods = [
    { method: 'Block Caving', metals: ['Copper', 'Gold'], depth: '500-1,500m', efficiency: 'High' },
    { method: 'Longwall Mining', metals: ['PGMs'], depth: '200-800m', efficiency: 'Very High' },
    { method: 'Open Pit', metals: ['Gold', 'Copper'], depth: '0-500m', efficiency: 'High' },
    { method: 'Room & Pillar', metals: ['PGMs', 'Gold'], depth: '100-400m', efficiency: 'Medium' },
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return '#4caf50';
      case 'Expanding': return '#2196f3';
      case 'Development': return '#ff9800';
      default: return '#D4AF37';
    }
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <AbstractBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section with Mining Image */}
        <Box
          sx={{
            minHeight: '55vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: 'url("/images/mining.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            backgroundRepeat: 'no-repeat',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 10 } }}>
            <Stack spacing={2}>
              <Chip
                label="WORLD-CLASS MINING"
                icon={<Factory sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.2),
                  color: '#D4AF37',
                  width: 'fit-content',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                }}
              >
                Responsible Mining Excellence
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#FFFFFF', 0.9),
                  maxWidth: 700,
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }}
              >
                Pentillion works with and alongside our strategic partners to deliver exceptional value 
                across the entire mining value chain — from exploration to refining, while maintaining 
                the highest ESG standards.
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Metrics Dashboard */}
        <Container maxWidth="xl" sx={{ py: 5 }}>
          <Grid container spacing={2.5}>
            {metrics.map((metric, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    animation: `${fadeInUp} 0.5s ease-out ${index * 0.1}s forwards`,
                    opacity: 0,
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      textAlign: 'center',
                      py: 2.5,
                      px: 1.5,
                      bgcolor: '#FFFFFF',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 2.5,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: alpha('#D4AF37', 0.4),
                        boxShadow: `0 10px 20px ${alpha('#D4AF37', 0.12)}`,
                      },
                    }}
                  >
                    <Box sx={{ color: '#D4AF37', fontSize: 36, mb: 1 }}>
                      {metric.icon}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#0a0a2a', fontSize: { xs: 28, md: 32 } }}>
                      {metric.value}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0a0a2a', mt: 0.5 }}>
                      {metric.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                      {metric.description}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600, display: 'block', mt: 0.5 }}>
                      {metric.detail}
                    </Typography>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Active Mines Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 8 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <PremiumHeader 
              title="Strategic Mining Portfolio" 
              subtitle="Our Operations" 
            />
            <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), maxWidth: 700, mx: 'auto', textAlign: 'center', mb: 5 }}>
              Strategically positioned across Africa's most prospective geological belts, 
              our mines deliver consistent, high-grade production
            </Typography>

            <Grid container spacing={3}>
              {mines.map((mine, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      animation: `${scaleIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                      opacity: 0,
                      height: '100%',
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        bgcolor: '#FFFFFF',
                        border: `1px solid ${alpha('#D4AF37', 0.12)}`,
                        borderRadius: 3,
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: alpha('#D4AF37', 0.35),
                          boxShadow: `0 15px 25px ${alpha('#D4AF37', 0.1)}`,
                        },
                      }}
                    >
                      {/* Status Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          zIndex: 1,
                        }}
                      >
                        <Chip
                          label={mine.status}
                          size="small"
                          sx={{
                            bgcolor: alpha(getStatusColor(mine.status), 0.12),
                            color: getStatusColor(mine.status),
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 22,
                          }}
                        />
                      </Box>

                      {/* Decorative header */}
                      <Box
                        sx={{
                          height: 80,
                          background: `linear-gradient(135deg, ${alpha('#D4AF37', 0.08)} 0%, ${alpha('#D4AF37', 0.02)} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderBottom: `1px solid ${alpha('#D4AF37', 0.1)}`,
                        }}
                      >
                        <Diamond sx={{ fontSize: 32, color: '#D4AF37', opacity: 0.5 }} />
                      </Box>
                      
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800, fontSize: '1.2rem', mb: 0.5 }}>
                          {mine.name}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                          <Typography variant="body1" sx={{ fontSize: '1rem' }}>{mine.flag}</Typography>
                          <LocationOn sx={{ fontSize: 12, color: '#D4AF37' }} />
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.6) }}>
                            {mine.location}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7), mb: 2, lineHeight: 1.5 }}>
                          {mine.description}
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                          {mine.metals.map((metal) => (
                            <Chip
                              key={metal}
                              label={metal}
                              size="small"
                              sx={{
                                mr: 0.5,
                                mb: 0.5,
                                bgcolor: alpha('#D4AF37', 0.1),
                                color: '#D4AF37',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 24,
                              }}
                            />
                          ))}
                        </Box>
                        
                        <Divider sx={{ my: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                              Annual Output
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                              {mine.annualOutput}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                              Reserves
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                              {mine.reserves}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                              Grade
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                              {mine.grade}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                              Mining Method
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                              {mine.miningMethod}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                              Depth
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                              {mine.depth}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block' }}>
                              Employees
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>
                              {mine.employees}
                            </Typography>
                          </Grid>
                        </Grid>
                        
                        <Divider sx={{ my: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
                            Since {mine.established}
                          </Typography>
                          <Button
                            size="small"
                            sx={{
                              color: '#D4AF37',
                              '&:hover': { bgcolor: alpha('#D4AF37', 0.08) },
                            }}
                          >
                            Learn More →
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Exploration Pipeline */}
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <PremiumHeader 
            title="Exploration Pipeline" 
            subtitle="Future Growth" 
          />
          <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto', textAlign: 'center', mb: 5 }}>
            Advanced exploration projects targeting next-generation mineral discoveries
          </Typography>

          <Grid container spacing={3}>
            {explorationProjects.map((project, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: alpha('#FFFFFF', 0.05),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: alpha('#D4AF37', 0.4),
                      bgcolor: alpha('#FFFFFF', 0.08),
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 800, mb: 1 }}>
                    {project.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOn sx={{ fontSize: 14, color: alpha('#FFFFFF', 0.5) }} />
                    <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5) }}>
                      {project.location}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    {project.targetMetals.map((metal) => (
                      <Chip
                        key={metal}
                        label={metal}
                        size="small"
                        sx={{
                          mr: 0.5,
                          bgcolor: alpha('#D4AF37', 0.15),
                          color: '#D4AF37',
                          fontSize: '0.6rem',
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 0.5 }}>
                    Estimated Resource: {project.estimatedResource}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), display: 'block', mb: 0.5 }}>
                    Stage: {project.stage}
                  </Typography>
                  <Chip
                    label={`Expected Production: ${project.expectedProduction}`}
                    size="small"
                    sx={{
                      mt: 1.5,
                      bgcolor: alpha('#D4AF37', 0.2),
                      color: '#D4AF37',
                      fontSize: '0.6rem',
                    }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Mining Methods */}
        <Box sx={{ bgcolor: '#FFFFFF', py: 6 }}>
          <Container maxWidth="xl">
            <PremiumHeader 
              title="Advanced Mining Methods" 
              subtitle="Technical Excellence" 
            />
            <Grid container spacing={3}>
              {miningMethods.map((method, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      bgcolor: '#FFFFFF',
                      border: `1px solid ${alpha('#D4AF37', 0.12)}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: alpha('#D4AF37', 0.4),
                      },
                    }}
                  >
                    <Engineering sx={{ color: '#D4AF37', fontSize: 40, mb: 1.5 }} />
                    <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 0.5 }}>
                      {method.method}
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block', mb: 1 }}>
                      Depth: {method.depth}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      {method.metals.map((metal) => (
                        <Chip
                          key={metal}
                          label={metal}
                          size="small"
                          sx={{
                            bgcolor: alpha('#D4AF37', 0.1),
                            color: '#D4AF37',
                            fontSize: '0.55rem',
                            height: 20,
                          }}
                        />
                      ))}
                    </Box>
                    <Chip
                      label={`Efficiency: ${method.efficiency}`}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: alpha('#4caf50', 0.1),
                        color: '#4caf50',
                        fontSize: '0.6rem',
                      }}
                    />
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Advanced Technologies Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.6s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="INNOVATION"
                    icon={<Speed sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.15),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, md: 36 },
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                    }}
                  >
                    Advanced Mining Technologies
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#FFFFFF', 0.8), mb: 4, lineHeight: 1.7 }}>
                    We leverage cutting-edge technology to enhance safety, efficiency, and 
                    environmental performance across all our mining operations.
                  </Typography>
                  
                  <Stack spacing={2.5}>
                    {technologies.map((tech, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          bgcolor: alpha('#FFFFFF', 0.05),
                          border: '1px solid rgba(212, 175, 55, 0.15)',
                          '&:hover': {
                            bgcolor: alpha('#FFFFFF', 0.08),
                            transform: 'translateX(8px)',
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: alpha('#D4AF37', 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#D4AF37',
                          }}
                        >
                          {tech.icon}
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                            {tech.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), display: 'block' }}>
                            {tech.description}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>
                            {tech.efficiency}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.6s ease-out 0.2s forwards`, opacity: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      bgcolor: '#FFFFFF',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    <WorkspacePremium sx={{ fontSize: 56, color: '#D4AF37', mb: 2 }} />
                    <Typography variant="h5" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 1 }}>
                      Technology Investment
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                      $250M ANNUAL R&D
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>Automation Rate</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>78%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={78}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha('#D4AF37', 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#D4AF37',
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>Renewable Energy</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>65%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={65}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha('#D4AF37', 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#D4AF37',
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>Digital Integration</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>92%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={92}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha('#D4AF37', 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#D4AF37',
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Stack>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* ESG & Sustainability Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.6s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="SUSTAINABILITY"
                    icon={<Verified sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.1),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, md: 36 },
                      fontWeight: 800,
                      color: '#0a0a2a',
                      mb: 2,
                    }}
                  >
                    Responsible Mining Excellence
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.7), mb: 3, lineHeight: 1.7 }}>
                    At Pentillion, we believe that exceptional value comes from responsible operations. 
                    Our mining practices exceed international standards for environmental protection, 
                    community engagement, and worker safety.
                  </Typography>
                  
                  <Stack spacing={2.5} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Shield sx={{ color: '#D4AF37' }} />
                      <Box>
                        <Typography variant="body1" sx={{ color: '#0a0a2a', fontWeight: 700 }}>Zero Harm Initiative</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
                          World-class safety standards with 3 years lost-time injury free across all operations
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Forest sx={{ color: '#D4AF37' }} />
                      <Box>
                        <Typography variant="body1" sx={{ color: '#0a0a2a', fontWeight: 700 }}>Carbon Neutral by 2026</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
                          Committed to sustainable operations and emissions reduction through renewable energy
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Handshake sx={{ color: '#D4AF37' }} />
                      <Box>
                        <Typography variant="body1" sx={{ color: '#0a0a2a', fontWeight: 700 }}>Community Investment</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
                          $10M+ invested annually in local education, healthcare, and infrastructure development
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                  
                  <Button
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                      color: '#0a0a2a',
                      px: 4,
                      py: 1.2,
                      fontWeight: 700,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    DOWNLOAD ESG REPORT
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.6s ease-out 0.2s forwards`, opacity: 0 }}>
                  <Grid container spacing={2}>
                    {sustainabilityMetrics.map((metric, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Card
                          elevation={0}
                          sx={{
                            p: 2.5,
                            textAlign: 'center',
                            bgcolor: '#F8F9FC',
                            border: `1px solid ${alpha('#D4AF37', 0.1)}`,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              borderColor: alpha('#D4AF37', 0.3),
                            },
                          }}
                        >
                          <Box sx={{ color: '#D4AF37', fontSize: 32, mb: 1 }}>
                            {metric.icon}
                          </Box>
                          <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 800 }}>
                            {metric.value}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700, mt: 0.5 }}>
                            {metric.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block', mb: 1 }}>
                            {metric.description}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={metric.progress}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                bgcolor: alpha('#D4AF37', 0.1),
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#D4AF37',
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 3,
                      p: 3,
                      bgcolor: alpha('#D4AF37', 0.03),
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Typography variant="body2" sx={{ color: '#0a0a2a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ThumbUp sx={{ color: '#4caf50', fontSize: 16 }} /> ✓ ISO 14001 Certified
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0a0a2a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ThumbUp sx={{ color: '#4caf50', fontSize: 16 }} /> ✓ Responsible Mining Initiative Member
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0a0a2a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ThumbUp sx={{ color: '#4caf50', fontSize: 16 }} /> ✓ UN Global Compact Signatory
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0a0a2a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ThumbUp sx={{ color: '#4caf50', fontSize: 16 }} /> ✓ LBMA Responsible Gold Certified
                      </Typography>
                    </Stack>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Certifications Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Chip
                label="CERTIFICATIONS"
                icon={<EmojiEvents sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Industry Recognition
              </Typography>
            </Box>
            
            <Grid container spacing={3} justifyContent="center">
              {[
                'LBMA Good Delivery',
                'ISO 9001:2024',
                'ISO 14001',
                'ISO 45001',
                'RJC Certified',
                'UN Global Compact',
                'Responsible Mining Initiative',
                'ICMM Member',
              ].map((cert, idx) => (
                <Grid item xs={6} sm={4} md={3} key={idx}>
                  <Card
                    elevation={0}
                    sx={{
                      textAlign: 'center',
                      p: 2.5,
                      bgcolor: alpha('#FFFFFF', 0.05),
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: alpha('#D4AF37', 0.4),
                        bgcolor: alpha('#FFFFFF', 0.08),
                      },
                    }}
                  >
                    <Verified sx={{ color: '#D4AF37', fontSize: 32, mb: 1 }} />
                    <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600, display: 'block' }}>
                      {cert}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 8 }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Card
              elevation={0}
              sx={{
                p: 5,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                borderRadius: 3,
              }}
            >
              <Diamond sx={{ fontSize: 56, color: '#D4AF37', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 1.5 }}>
                Partner With Us
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mb: 3, maxWidth: 500, mx: 'auto' }}>
                Explore partnership opportunities for off-take agreements, joint ventures, 
                and strategic investment in our mining operations
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  color: '#0a0a2a',
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 20px ${alpha('#D4AF37', 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Mining Division
              </Button>
            </Card>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Mining;